/** Lane C0-PROPS: helpers the prop painters share (props.ts, props2.ts). */
import { hash2 } from '@nine/shared';
import { box, M, snap, type Proj, type Ramp, type RGB } from '../buildings';
import type { Sprite } from '../scenery';
import { blob, kitFinish, propPix } from './parts';

export type Pp = ReturnType<typeof propPix>;
export const fin = (p: Pp): Sprite => kitFinish(p.px, p.ax, p.ay);
export const wood =
  (l0 = 0, r: Ramp = M.wood): ((u: number, v: number, l: number) => RGB) =>
  (u, v, l) =>
    snap(r, l + l0 + ((v | 0) % 9 === 0 ? -0.12 : 0), u | 0, v | 0);
export const stoneT = (u: number, v: number, l: number): RGB => snap(M.stone, l + (hash2(u | 0, v | 0) - 0.5) * 0.12, u | 0, v | 0, 0.2);
/** A flat colour per face from a ramp, lit by the face. */
export const flat =
  (r: Ramp, l0 = 0): ((u: number, v: number, l: number) => RGB) =>
  (u, v, l) =>
    snap(r, l + l0, u | 0, v | 0, 0.15);

/** A square post (tile units r across) from z0 to z1 at (x, y) in a prop's frame. */
export function pole(P: Proj, p: Pp, x: number, y: number, r: number, z0: number, z1: number, mat: Ramp = M.wood): void {
  box(p.px, P, [x - r, y - r, x + r, y + r], z0, z1, (u, v, l) => snap(mat, l + (v > z1 - 2 ? 0.15 : 0), u | 0, v | 0));
}

/** A round-bellied jar standing at screen (x, y): `h` tall, `r` wide, a rim and a lit side. */
export function jarAt(p: Pp, x: number, y: number, h: number, r: number, mat: Ramp, glaze = false): void {
  for (let b = 0; b < h; b++) {
    const t = b / h;
    const w = r * (0.55 + Math.sin(Math.min(1, t * 1.15) * Math.PI) * 0.5);
    for (let a = -Math.round(w); a <= Math.round(w); a++) {
      const l = 0.5 + (a / (w + 1)) * 0.28 + (t > 0.85 ? 0.1 : 0) + (glaze && Math.abs(a - w * 0.4) < 1 ? 0.25 : 0);
      p.px.set(x + a, y - b, snap(mat, l, x + a, b, 0.2));
    }
  }
  p.px.rect(x - Math.round(r * 0.5), y - h - 1, Math.round(r) + 1, 1, mat[1]!);
}

/** A barrel at screen (x, y): staves, two iron hoops, a lit top. */
export function barrelAt(p: Pp, x: number, y: number, h = 14, r = 5): void {
  for (let b = 0; b < h; b++) {
    const w = r - Math.abs(b - h / 2) * 0.12;
    for (let a = -Math.round(w); a <= Math.round(w); a++) {
      const hoop = b === 2 || b === h - 3;
      p.px.set(x + a, y - b, hoop ? M.dark[2]! : snap(M.wood, 0.5 + (a / (w + 1)) * 0.3 + (((a + 20) % 3) === 0 ? -0.12 : 0), a, b, 0));
    }
  }
  blob(p.px, x, y - h, r - 0.5, 1.6, M.deck, 0.65);
}
